import { Nullable } from '@common/types/utility-types';
import { cloneDeep } from 'lodash-es';
import { Guid } from '@common/models/guid';
import { Moment } from 'moment';
import { TrackByFunction } from '@angular/core';

export interface DataHistory<TDateType extends string | Moment = string> {
  createdOn: TDateType;
  createdBy: string;
  lastModifiedOn: TDateType;
  lastModifiedBy: string;
}

export interface Identifiable<TId extends string | number = string> {
  id: TId;

  clone(): this;

  is(model?: Identifiable);
}

/**
 * Base class for every model that contains data.
 * Provides helper methods for equality comparrison, cloning, freezing, ...
 * Contains the typical fields the EntityBase class in the backend containes (id, createdOn, ...)
 */
export abstract class DataModel<
  TId extends string | number = string,
  TDateType extends string | Moment = string
> implements DataHistory<TDateType>, Identifiable<TId>
{
  protected constructor(public readonly id: TId, history?: Partial<DataHistory<TDateType>>) {
    this.createdBy = history?.createdBy || '';
    this.createdOn = history?.createdOn || undefined;
    this.lastModifiedBy = history?.lastModifiedBy || '';
    this.lastModifiedOn = history?.lastModifiedOn || undefined;
  }

  public readonly createdOn: TDateType;
  public readonly createdBy: string;
  public readonly lastModifiedOn: Nullable<TDateType>;
  public readonly lastModifiedBy: Nullable<string>;

  public clone(): any {
    return cloneDeep(this);
  }

  /**
   * Returns whether the given model has the same id
   * @param model - the model to compare with
   */
  public is(model: Identifiable | undefined): boolean {
    return this.id === model?.id;
  }

  public hasEmpty(key: keyof any): boolean {
    return this[key] === undefined || this[key] === null || (this[key] as any) === '';
  }

  /**
   * The exact opposite of the @is method
   */
  public isNot(model?: DataModel): boolean {
    return !this.is(model);
  }

  /**
   * Mutates the model with all properties from the given object without references
   * @param otherObjectFromSameClass - the object to copy form
   */
  public copyFrom(otherObjectFromSameClass: any): void {
    Object.keys(otherObjectFromSameClass).forEach(k => {
      if (typeof this[k] !== 'function') {
        this[k] = cloneDeep(otherObjectFromSameClass[k]);
      }
    });
  }

  public freeze(): void {
    Object.freeze(this);
  }

  /**
   * Returns whether the model is default
   * For example when the id is an empty string or default Guid
   */
  get isDefault(): boolean {
    return !this.id || this.id === Guid.empty();
  }

  /**
   * The exact opposite of the @isDefault getter
   */
  get isNotDefault(): boolean {
    return !this.isDefault;
  }

  static track: TrackByFunction<DataModel> = (index: number, item: DataModel) => item.id;
}
